/**
 * SEO utility functions for updating meta tags dynamically
 */

const DEFAULT_TITLE = 'SphynxPlace.com – Buy, Sell & Adopt Sphynx Cats';
const DEFAULT_DESCRIPTION = 'SphynxPlace.com - Find and adopt Sphynx cats. Browse listings, connect with breeders, and give a Sphynx cat a loving home.';
const DEFAULT_IMAGE = '/build/favicon-96x96.png';
const SITE_NAME = 'SphynxPlace.com';
const SITE_URL = window.location.origin;

/**
 * Update or create a meta tag
 */
const updateMetaTag = (property, content, isProperty = false) => {
  if (!content) return;

  const selector = isProperty 
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  
  let meta = document.querySelector(selector);
  
  if (!meta) {
    meta = document.createElement('meta');
    if (isProperty) {
      meta.setAttribute('property', property);
    } else {
      meta.setAttribute('name', property);
    }
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

/**
 * Update or create link tag
 */
const updateLinkTag = (rel, href) => {
  if (!href) return;

  let link = document.querySelector(`link[rel="${rel}"]`);
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', href);
};

/**
 * Remove meta tags by selector
 */
const removeMetaTags = (selector) => {
  document.querySelectorAll(selector).forEach(tag => tag.remove());
};

/**
 * Update structured data (JSON-LD)
 */
const updateStructuredData = (data, id = 'structured-data') => {
  // Remove existing structured data
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  if (!data) return;

  // Handle array of structured data objects
  const dataArray = Array.isArray(data) ? data : [data];
  
  dataArray.forEach((item, index) => {
    if (!item) return;
    
    const scriptId = dataArray.length > 1 ? `${id}-${index}` : id;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(item, null, 0);
    document.head.appendChild(script);
  });
};

/**
 * Update SEO meta tags
 * @param {Object} seoData - SEO data object
 */
export const updateSEO = (seoData = {}) => {
  const {
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_IMAGE,
    url = window.location.href,
    type = 'website',
    keywords,
    robots = 'index, follow',
    structuredData,
    article,
  } = seoData;

  // Update title
  document.title = title;

  // Update meta description
  updateMetaTag('description', description);

  // Update keywords if provided
  if (keywords) {
    updateMetaTag('keywords', keywords);
  }

  // Update robots
  updateMetaTag('robots', robots);

  // Update canonical URL
  updateLinkTag('canonical', url);

  // Update Open Graph tags
  updateMetaTag('og:title', title, true);
  updateMetaTag('og:description', description, true);
  updateMetaTag('og:image', image.startsWith('http') ? image : `${SITE_URL}${image}`, true);
  updateMetaTag('og:url', url, true);
  updateMetaTag('og:type', type, true);
  updateMetaTag('og:site_name', SITE_NAME, true);
  updateMetaTag('og:locale', 'en_US', true);

  // Update article meta tags if provided
  if (article) {
    if (article.published_time) {
      updateMetaTag('article:published_time', article.published_time, true);
    }
    if (article.modified_time) {
      updateMetaTag('article:modified_time', article.modified_time, true);
    }
    if (article.author) {
      updateMetaTag('article:author', article.author, true);
    }
    if (article.section) {
      updateMetaTag('article:section', article.section, true);
    }
    if (article.tags && Array.isArray(article.tags)) {
      // Remove existing article tags
      removeMetaTags('meta[property^="article:tag"]');
      // Add new article tags
      article.tags.forEach(tag => {
        updateMetaTag('article:tag', tag, true);
      });
    }
  } else {
    // Remove article tags if not provided
    removeMetaTags('meta[property^="article:"]');
  }

  // Update Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', image.startsWith('http') ? image : `${SITE_URL}${image}`);

  // Update structured data
  if (structuredData) {
    updateStructuredData(structuredData);
  }
};

/**
 * Reset SEO to default values
 */
export const resetSEO = () => {
  updateSEO({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_IMAGE,
    url: SITE_URL,
    type: 'website',
    robots: 'index, follow',
  });
};

